import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../i18n";
import { ManagementDialog, ManagementPanel } from "../managementUI";
import {
  components,
  date,
  duration,
  fresh,
  number,
  reportHealth,
  stateLabel,
  type MonitoringData,
} from "./model";
import { Badge, Empty, Note, Summary, Table } from "./ui";

export function HealthOverview({
  data,
  now,
}: {
  data: MonitoringData;
  now: number;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const openAlerts = (alert?: string) => {
    const next = new URLSearchParams(params);
    next.delete("section");
    next.set("alerts_tab", "active");
    if (alert) next.set("alert", alert);
    else next.delete("alert");
    navigate(`/alerts?${next}`);
  };
  const rows = components(data, now, t);
  const state = reportHealth(rows, data.alerts, now);
  const faults = rows.filter(
    (row) => row.state === "critical" || row.state === "degraded",
  );
  const unknown = rows.filter((row) => row.state === "unknown");
  const alerts =
    fresh(data.alerts, now) && data.alerts.data?.alerts_available
      ? data.alerts.data.items.filter((a) => a.state === "active")
      : null;
  const placement = fresh(data.placement, now) ? data.placement.data : null;
  const sandbox = fresh(data.sandbox, now) ? data.sandbox.data : null;
  return (
    <>
      <section className="monitor-report">
        <div>
          <h2>{t("当前健康报告", "Current health report")}</h2>
          <p>
            {t(
              "由组件状态、已知告警和数据覆盖共同判断。",
              "Based on component health, known alerts, and data coverage.",
            )}
          </p>
        </div>
        <Badge state={state} />
      </section>
      <Summary
        items={[
          {
            label: t("异常组件", "Components needing attention"),
            value: String(faults.length),
          },
          {
            label: t("触发中告警", "Firing alerts"),
            value: alerts ? String(alerts.length) : "—",
          },
          {
            label: t("已知状态 / 组件", "Known status / components"),
            value: `${rows.length - unknown.length} / ${rows.length}`,
          },
          {
            label: t("可路由 Team", "Routable Teams"),
            value: placement
              ? `${placement.placements.filter((p) => p.route_eligible).length} / ${placement.placements.length}`
              : "—",
          },
        ]}
      />
      <ManagementPanel
        title={t("优先处理", "Needs attention")}
        actions={
          <button className="secondary-button" onClick={() => openAlerts()}>
            {t("进入告警中心", "Open alerts")}
          </button>
        }
      >
        {!faults.length && !alerts?.length ? (
          <Empty>
            {unknown.length
              ? t(
                  "暂未发现已知故障，仍有数据缺口。",
                  "No known faults detected; observation gaps remain.",
                )
              : t("当前没有已知故障。", "No known faults.")}
          </Empty>
        ) : (
          <Table
            headers={[
              t("对象", "Object"),
              t("状态", "State"),
              t("证据 / 影响", "Evidence / impact"),
              t("查看", "Details"),
            ]}
          >
            {faults.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td>
                  <Badge state={row.state} />
                </td>
                <td>{row.detail}</td>
                <td>
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setParams({ section: "components", component: row.id })
                    }
                  >
                    {t("组件详情", "Component details")}
                  </button>
                </td>
              </tr>
            ))}
            {alerts?.map((alert) => (
              <tr key={alert.fingerprint}>
                <td>{alert.labels.alertname}</td>
                <td>
                  <Badge
                    state={
                      alert.labels.severity === "critical"
                        ? "critical"
                        : "degraded"
                    }
                  />
                </td>
                <td>
                  {alert.annotations.summary}
                  <span>{alert.labels.instance || alert.labels.job}</span>
                </td>
                <td>
                  <button
                    className="secondary-button"
                    onClick={() => openAlerts(alert.fingerprint)}
                  >
                    {t("告警详情", "Alert details")}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </ManagementPanel>
      <div className="monitor-columns">
        <ManagementPanel title={t("当前容量", "Current capacity")}>
          <Table headers={[t("资源", "Resource"), t("当前分配", "Allocation")]}>
            <tr>
              <td>Pacture API</td>
              <td>
                {placement
                  ? `${placement.instances.reduce((n, i) => n + i.loaded_team_count, 0)} / ${placement.instances.reduce((n, i) => n + i.max_team_slots, 0)} Team slots`
                  : "—"}
              </td>
            </tr>
            <tr>
              <td>Sandbox</td>
              <td>
                {sandbox
                  ? `${number(sandbox.fleet.totals.pod_count)} ${t("容器", "containers")}`
                  : "—"}
              </td>
            </tr>
            <tr>
              <td>Sandbox CPU / {t("内存", "memory")}</td>
              <td>
                {sandbox
                  ? `${number(sandbox.fleet.totals.allocated_cpu_milli / 1000)} vCPU / ${number(sandbox.fleet.totals.allocated_mem_mib / 1024)} GiB`
                  : "—"}
              </td>
            </tr>
          </Table>
        </ManagementPanel>
        <ManagementPanel title={t("观测缺口", "Observation gaps")}>
          {unknown.length ? (
            <ul className="monitor-list">
              {unknown.map((row) => (
                <li key={row.id}>
                  <strong>{row.name}</strong>
                  <span>{row.detail}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>
              {t(
                "组件健康数据已覆盖。",
                "Component health sources are covered.",
              )}
            </Empty>
          )}
          {!fresh(data.alerts, now) || !data.alerts.data?.alerts_available ? (
            <Note>
              {t(
                "告警源不可用或已过期。",
                "Alert source is unavailable or stale.",
              )}
            </Note>
          ) : null}
        </ManagementPanel>
      </div>
      <ManagementPanel title={t("最近管理变更", "Recent management changes")}>
        {!fresh(data.changes, now) || !data.changes.data?.available ? (
          <Empty>
            {t("变更记录暂不可用。", "Change records are unavailable.")}
          </Empty>
        ) : !data.changes.data.items.length ? (
          <Empty>{t("尚无操作记录。", "No operations recorded.")}</Empty>
        ) : (
          <Table
            headers={[
              t("操作", "Operation"),
              t("目标", "Target"),
              t("结果", "Result"),
              t("更新时间", "Updated"),
            ]}
          >
            {data.changes.data.items.slice(0, 8).map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.kind}</strong>
                  <span>{item.reason}</span>
                </td>
                <td>{item.resource_id}</td>
                <td>{stateLabel(item.state, t)}</td>
                <td>{date(item.updated_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </ManagementPanel>
    </>
  );
}

export function ComponentMonitoring({
  data,
  now,
}: {
  data: MonitoringData;
  now: number;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const rows = components(data, now, t);
  const rank = { critical: 0, degraded: 1, unknown: 2, healthy: 3 };
  const items = rows
    .filter(
      (row) =>
        (filter === "all" || row.state === filter) &&
        row.name.toLowerCase().includes(search.toLowerCase()),
    )
    .sort(
      (a, b) => rank[a.state] - rank[b.state] || a.name.localeCompare(b.name),
    );
  const selected = rows.find((row) => row.id === params.get("component"));
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("component");
    setParams(next, { replace: true });
  };
  const metrics = fresh(data.telemetry, now)
    ? data.telemetry.data?.metrics
    : null;
  return (
    <>
      <ManagementPanel title={t("组件状态", "Component health")}>
        <div className="monitor-toolbar">
          <label>
            {t("搜索组件", "Search components")}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="TiDB / Sandbox…"
            />
          </label>
          <label>
            {t("状态", "State")}
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">{t("全部", "All")}</option>
              <option value="critical">{t("异常", "Critical")}</option>
              <option value="degraded">{t("需关注", "Degraded")}</option>
              <option value="unknown">{t("未知", "Unknown")}</option>
              <option value="healthy">{t("正常", "Healthy")}</option>
            </select>
          </label>
        </div>
        {!items.length ? (
          <Empty>{t("没有匹配的组件。", "No matching components.")}</Empty>
        ) : (
          <Table
            headers={[
              t("组件", "Component"),
              t("健康", "Health"),
              t("状态说明", "Evidence"),
              t("容量 / 实例", "Capacity / instances"),
              t("观测时间", "Observed"),
              t("操作", "Actions"),
            ]}
          >
            {items.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.name}</strong>
                  <span>
                    {row.id === "ids"
                      ? t("全局依赖", "Global dependency")
                      : row.id}
                  </span>
                </td>
                <td>
                  <Badge state={row.state} />
                </td>
                <td>{row.detail}</td>
                <td>{row.capacity}</td>
                <td>{date(row.observedAt)}</td>
                <td>
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setParams({ section: "components", component: row.id })
                    }
                  >
                    {t("详情", "Details")}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </ManagementPanel>
      <ManagementPanel
        title={t(
          "已接入服务 HTTP 性能 · 最近5分钟",
          "Instrumented HTTP performance · last 5 minutes",
        )}
      >
        <Note>
          {t(
            "按采集口径分别展示。这里的延迟不是任务完成时间。",
            "Shown separately by instrumentation family. These latencies do not measure task completion time.",
          )}
        </Note>
        <Table
          headers={[
            t("采集口径", "Instrumentation"),
            t("请求 / 秒", "Requests / second"),
            "P95",
          ]}
        >
          <tr>
            <td>Go HTTP</td>
            <td>{number(metrics?.go_request_rate)}</td>
            <td>{duration(metrics?.go_request_p95_seconds)}</td>
          </tr>
          <tr>
            <td>Python HTTP</td>
            <td>{number(metrics?.python_request_rate)}</td>
            <td>{duration(metrics?.python_request_p95_seconds)}</td>
          </tr>
        </Table>
      </ManagementPanel>
      {selected ? (
        <ManagementDialog title={selected.name} onClose={close}>
          <Badge state={selected.state} />
          <p>{selected.detail}</p>
          <dl className="monitor-detail">
            <dt>{t("观测时间", "Observed")}</dt>
            <dd>{date(selected.observedAt)}</dd>
            <dt>{t("容量", "Capacity")}</dt>
            <dd>{selected.capacity}</dd>
          </dl>
          {selected.id === "pacture-api" ? (
            <ul className="monitor-list">
              {data.placement.data?.instances.map((i) => (
                <li key={i.instance_id}>
                  <strong>
                    {i.instance_id} · {i.lifecycle}
                  </strong>
                  <span>
                    {i.loaded_team_count}/{i.max_team_slots} Team slots ·{" "}
                    {i.release_version}
                  </span>
                  <span>
                    {t("心跳", "Heartbeat")}: {date(i.heartbeat_at)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {selected.id === "telemetry" ? (
            <dl className="monitor-detail">
              <dt>{t("最大导出队列", "Max export queue")}</dt>
              <dd>{number(metrics?.max_export_queue)}</dd>
              <dt>{t("失败记录 · 15分钟", "Failed exports · 15 minutes")}</dt>
              <dd>{number(metrics?.export_failures_15m)}</dd>
              <dt>{t("样本距今", "Sample age")}</dt>
              <dd>{duration(metrics?.data_freshness_seconds)}</dd>
            </dl>
          ) : null}
          <div className="dialog-actions">
            {selected.href ? (
              <Link className="secondary-button" to={selected.href}>
                {t("进入管理页面", "Open management")}
              </Link>
            ) : null}
            <button className="secondary-button" onClick={close}>
              {t("关闭", "Close")}
            </button>
          </div>
        </ManagementDialog>
      ) : null}
    </>
  );
}
